import React, { useEffect, useMemo, useState } from 'react';
import { calculateLaborHours } from '../../SiteTakeoff/laborCalculator';
import {
  AREA_TYPE_COLORS,
  AREA_TYPE_LABELS,
  DEFAULT_PRODUCTION_RATES,
  RATE_UNIT_LABELS,
} from '../../SiteTakeoff/siteTakeoffConstants';
import {
  DEFAULT_ESTIMATE_SETTINGS,
  buildServiceLinesFromTakeoff,
  computeService,
  computeTier,
  getFrequency,
  getVisitsPerYear,
  runSanityChecks,
  solveMarginForTarget,
} from './estimateEngine';
import './takeoffEstimateWorkspace.css';

const fmtCurrency = (value, maxDigits = 0) =>
  (Number(value) || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: maxDigits,
    minimumFractionDigits: maxDigits,
  });

const fmtNumber = (value, maxDigits = 1) =>
  (Number(value) || 0).toLocaleString('en-US', {
    maximumFractionDigits: maxDigits,
    minimumFractionDigits: maxDigits,
  });

const roleClass = {
  Maint: 'maint',
  Arborist: 'arb',
  Herbicide: 'herb',
  Irrigation: 'irrig',
};

const roleShort = {
  Maint: 'Maint',
  Arborist: 'Arborist',
  Herbicide: 'Herbicide',
  Irrigation: 'Irrigation',
};

const defaultTiers = [
  {
    id: 'essential',
    name: 'Essential',
    recommended: false,
    marginPct: 40,
    includedServices: ['Maint', 'Arborist'],
    targetPrice: { amount: 7800, basis: 'monthly' },
  },
  {
    id: 'standard',
    name: 'Standard',
    recommended: true,
    marginPct: 50,
    includedServices: ['Maint', 'Arborist', 'Herbicide', 'Irrigation'],
    targetPrice: { amount: 10000, basis: 'monthly' },
  },
  {
    id: 'premium',
    name: 'Premium',
    recommended: false,
    marginPct: 55,
    includedServices: ['Maint', 'Arborist', 'Herbicide', 'Irrigation'],
    targetPrice: { amount: '', basis: 'monthly' },
  },
];

const getJobsiteDisplay = (jobsite) => {
  const name = jobsite?.name || jobsite?.propertyName || 'New estimate';
  const code = jobsite?.number || jobsite?.jobNumber || jobsite?.aspirePropertyId || jobsite?._id || 'draft';
  const cityState = [jobsite?.city, jobsite?.state].filter(Boolean).join(', ');
  return { name, code, cityState };
};

const getTotalSF = (areas = []) =>
  areas
    .filter(area => !area.parentAreaId)
    .reduce((sum, area) => sum + (Number(area.sqft ?? area.area) || 0), 0);

const buildRateOverrides = (seed = {}) => {
  const rates = seed.rates || {};
  return {
    ...DEFAULT_ESTIMATE_SETTINGS.laborRates,
    Maint: Number(rates.mtc) || DEFAULT_ESTIMATE_SETTINGS.laborRates.Maint,
    Arborist: Number(rates.arborist) || DEFAULT_ESTIMATE_SETTINGS.laborRates.Arborist,
    Herbicide: Number(rates.herbicide) || DEFAULT_ESTIMATE_SETTINGS.laborRates.Herbicide,
    Irrigation: Number(rates.irrigation) || DEFAULT_ESTIMATE_SETTINGS.laborRates.Irrigation,
  };
};

const cloneEstimateSettings = (seed = {}) => ({
  ...DEFAULT_ESTIMATE_SETTINGS,
  laborRates: buildRateOverrides(seed),
  productionRates: Object.entries(DEFAULT_ESTIMATE_SETTINGS.productionRates).reduce((acc, [key, value]) => ({
    ...acc,
    [key]: { ...value },
  }), {}),
  frequencies: Object.entries(DEFAULT_ESTIMATE_SETTINGS.frequencies).reduce((acc, [key, value]) => ({
    ...acc,
    [key]: { ...value },
  }), {}),
  modifierTable: { ...DEFAULT_ESTIMATE_SETTINGS.modifierTable },
  irrigationZoneInference: { ...DEFAULT_ESTIMATE_SETTINGS.irrigationZoneInference },
  subcontractors: {
    preEmergent: { ...DEFAULT_ESTIMATE_SETTINGS.subcontractors.preEmergent },
  },
});

const toLaborCalculatorModifiers = (modifierTable = {}) => ({
  access_obstructed: Number(modifierTable.obstructed) || 1,
  slope_sloped: Number(modifierTable.sloped) || 1,
  litterExposure_high: Number(modifierTable['high-litter']) || 1,
  weedPressure_med: Number(modifierTable['med-weed']) || 1,
  weedPressure_high: Number(modifierTable['high-weed']) || 1,
  limitedAccess: Number(modifierTable['limited-access']) || 1,
  blowRestrictions: Number(modifierTable['blow-restrictions']) || 1,
});

const Button = ({ className = '', children, ...props }) => (
  <button type="button" className={`te-btn ${className}`} {...props}>{children}</button>
);

const StatusPill = () => (
  <span className="te-status-pill"><span className="dot" /> Draft - autosaved 2m ago</span>
);

const FrequencyPill = ({ frequency, settings }) => {
  const preset = getFrequency(frequency, settings);
  return (
    <span className={`te-freq-pill ${preset.family || 'yr'}`}>
      {preset.label} <span className="car">v</span>
    </span>
  );
};

const CoveragePill = ({ coverage }) => {
  const pct = Math.round((Number(coverage) || 0) * 100);
  const full = pct >= 100;
  return (
    <span className={`te-cov ${full ? 'full' : 'partial'}`}>
      {full ? '100%' : `${pct}% cov`} {!full && <span className="car">v</span>}
    </span>
  );
};

const TaskRow = ({ task, settings }) => {
  const visits = getVisitsPerYear(task.frequency, settings);
  const annual = Number(task.annualHours) || (Number(task.hoursPerVisit) || 0) * visits;
  return (
    <div className={`te-task ${task.flag ? 'flag' : ''}`}>
      <div className="lbl">
        {task.label}
        <span className="areas">({task.areaCount || 0}{task.serviceCode === 'irrigation_check' ? ' zones' : ' areas'})</span>
      </div>
      <div className="h">
        {fmtNumber(task.hoursPerVisit, 2)}h <CoveragePill coverage={task.coverage ?? 1} />
        {visits < 52 && <> x <FrequencyPill frequency={task.frequency} settings={settings} /></>}
      </div>
      <div className="ann">x {fmtNumber(visits, 0)} = {fmtNumber(annual, 1)}</div>
    </div>
  );
};

const FrequencyGroup = ({ title, frequency, tasks, settings, note }) => {
  if (!tasks?.length) return null;
  return (
    <div className="te-freq-group">
      <div className="te-freq-head">
        {title}
        {frequency && <FrequencyPill frequency={frequency} settings={settings} />}
        {note && <span className="muted-note">{note}</span>}
      </div>
      <div className="tasks">
        {tasks.map(task => <TaskRow key={`${task.serviceCode}-${task.frequency?.label || task.frequency}`} task={task} settings={settings} />)}
      </div>
    </div>
  );
};

const BreakdownRows = ({ rows = [] }) => (
  <div className="te-breakdown-card">
    {rows.map((row) => (
      <div className="te-breakdown-row" key={row.label}>
        <span className="swab" style={{ background: row.color }} />
        <span className="te-breakdown-label">
          {row.label}
          {row.areas != null && <span className="areas"> ({row.areas} areas)</span>}
          {row.math && <span className="areas"> {row.math}</span>}
        </span>
        <b>{row.sqft != null ? `${fmtNumber(row.sqft, 0)} SF` : `${fmtNumber(row.quantity, 0)} zones`}</b>
      </div>
    ))}
  </div>
);

const InferredPreEmergent = ({ service, settings }) => {
  const sub = service.sub || settings.subcontractors.preEmergent;
  const visits = getFrequency(sub.frequency, settings);
  return (
    <div className="te-subline">
      <div className="lbl">
        <span className="te-inferred-badge">Inferred</span>
        {fmtNumber(service.inference.totalAcres, 2)} acres of pre-emergent area
        <span className="from">- from takeoff area-types</span>
        <Button className="ghost mini">Edit assumptions</Button>
      </div>
      <BreakdownRows rows={service.inference.breakdown} />
      <div className="te-breakdown-total">
        <span>Total pre-emergent area</span>
        <b>{fmtNumber(service.inference.totalSF, 0)} SF / 43,560 = {fmtNumber(service.inference.totalAcres, 2)} ac</b>
      </div>
      <div className="te-sub-calc">
        <span className="vendor">{sub.vendorName || 'Vendor'}</span>
        <span className="num">{fmtNumber(service.inference.totalAcres, 2)} ac</span>
        <span className="op">x</span>
        <span className="num">{fmtCurrency(sub.unitRate)} / ac</span>
        <span className="op">=</span>
        <span className="num">{fmtCurrency(service.inference.perVisitCost)} / visit</span>
        <span className="op">(min {fmtCurrency(sub.minPerVisit)} - {service.inference.minApplied ? 'applied' : 'not applied'})</span>
        <span className="op">x</span>
        <FrequencyPill frequency={sub.frequency} settings={settings} />
        <span className="eq">= {fmtCurrency(service.inference.annualCost)}</span>
      </div>
      <div className="te-help">Vendor rate, divisors and minimums are set in Settings - Subcontractors.</div>
    </div>
  );
};

const InferredIrrigation = ({ service, settings }) => {
  const inference = service.inference;
  if (!inference) return null;
  return (
    <div className="te-subline">
      <div className="lbl">
        <span className="te-inferred-badge">Inferred</span>
        {fmtNumber(inference.totalZones, 0)} zones to inspect
        <span className="from">- from takeoff counts</span>
        <Button className="ghost mini">Edit assumptions</Button>
      </div>
      <BreakdownRows rows={inference.breakdown} />
      <div className="te-breakdown-total">
        <span>Total zones</span>
        <b>{fmtNumber(inference.totalZones, 0)} zones x {fmtNumber(inference.minutesPerZone, 0)} min/zone</b>
      </div>
    </div>
  );
};

const ServiceBlock = ({ service, settings, onToggle }) => {
  const result = computeService(service, settings);
  const summary = service.delivery === 'subcontracted'
    ? `${fmtCurrency(result.cost)} / yr`
    : `${fmtNumber(result.hours, 1)} hrs/yr`;
  const routineNote = service.role === 'Maint' && service.routineTasks?.length
    ? `${fmtNumber(service.routineTasks.reduce((sum, task) => sum + (Number(task.hoursPerVisit) || 0), 0), 2)} manhours`
    : null;

  return (
    <div className={`te-svc ${roleClass[service.role] || ''} ${service.enabled ? '' : 'off'}`}>
      <div className="row">
        <div className="swatch" />
        <div className="name">
          {service.name || service.role}
          {service.delivery === 'subcontracted' && <span className="te-sub-badge">Subcontracted</span>}
        </div>
        <div className={`hrs ${service.delivery === 'subcontracted' ? 'sub-cost' : ''}`}>{summary}</div>
        <button
          type="button"
          aria-label={`Toggle ${service.name || service.role}`}
          className="toggle"
          onClick={() => onToggle(service.role)}
        />
      </div>
      {service.delivery === 'subcontracted' && service.inference && (
        <InferredPreEmergent service={service} settings={settings} />
      )}
      {service.role === 'Irrigation' && service.inference && (
        <InferredIrrigation service={service} settings={settings} />
      )}
      <FrequencyGroup
        title="Every visit"
        frequency="weekly"
        tasks={service.routineTasks}
        settings={settings}
        note={routineNote}
      />
      <FrequencyGroup
        title="Per application"
        tasks={service.periodicTasks}
        settings={settings}
      />
    </div>
  );
};

const TargetInput = ({ tier, onTargetChange }) => {
  const value = tier.targetPrice?.amount ?? '';
  const basis = tier.targetPrice?.basis || 'monthly';
  return (
    <div className="target">
      <label>Target</label>
      <input
        value={value}
        placeholder="-"
        onChange={(event) => onTargetChange({ amount: event.target.value, basis })}
      />
      <div className="seg" aria-label={`${tier.name} target basis`}>
        <button
          type="button"
          className={basis === 'per-visit' ? 'on' : ''}
          onClick={() => onTargetChange({ amount: value, basis: 'per-visit' })}
        >
          /visit
        </button>
        <button
          type="button"
          className={basis === 'monthly' ? 'on' : ''}
          onClick={() => onTargetChange({ amount: value, basis: 'monthly' })}
        >
          /mo
        </button>
      </div>
    </div>
  );
};

const TierCard = ({ tier, estimate, settings, onTierChange }) => {
  const computed = computeTier(estimate, tier, settings);
  const solvedMargin = solveMarginForTarget(estimate, tier, settings);
  const hasTarget = Number(tier.targetPrice?.amount) > 0;

  const toggleService = (role) => {
    const included = tier.includedServices.includes(role)
      ? tier.includedServices.filter(item => item !== role)
      : [...tier.includedServices, role];
    onTierChange({ ...tier, includedServices: included });
  };

  return (
    <div className={`te-tier ${tier.recommended ? 'featured' : ''}`}>
      <div className="tname">
        {tier.name}
        {tier.recommended && <span className="badge">Recommended</span>}
      </div>
      <div className="price">
        {fmtCurrency(computed.annualPrice)}
        <span className="ann">annual</span>
      </div>
      <div className="mo"><b>{fmtCurrency(computed.monthlyPrice)}</b> /mo</div>
      <TargetInput
        tier={tier}
        onTargetChange={(targetPrice) => onTierChange({ ...tier, targetPrice })}
      />
      {hasTarget && solvedMargin != null && (
        <button
          type="button"
          className="solve"
          onClick={() => onTierChange({ ...tier, marginPct: solvedMargin })}
        >
          Solve margin to hit {tier.targetPrice.basis === 'monthly'
            ? `${fmtCurrency(tier.targetPrice.amount)}/mo`
            : `${fmtCurrency(tier.targetPrice.amount)}/visit`} - <b>{fmtNumber(solvedMargin, 1)}%</b>
        </button>
      )}
      <div className="knobs">
        <div className="knob">
          Margin
          <input
            className="val"
            value={tier.marginPct}
            onChange={(event) => onTierChange({ ...tier, marginPct: Number(event.target.value) || 0 })}
          />
        </div>
        <div className="knob">Rates <span className="val readonly">Default</span></div>
      </div>
      <div className="incl">
        {['Maint', 'Arborist', 'Herbicide', 'Irrigation'].map(role => (
          <button
            type="button"
            key={role}
            className={`chip ${tier.includedServices.includes(role) ? '' : 'off'}`}
            onClick={() => toggleService(role)}
          >
            {roleShort[role]}
          </button>
        ))}
      </div>
    </div>
  );
};

const RatesStrip = ({ settings, services, onSettingsClick }) => {
  const herbSub = services.find(service => service.role === 'Herbicide' && service.delivery === 'subcontracted')?.sub;
  return (
    <>
      <div className="te-rates">
        <div className="rate"><div className="l">Maintenance</div><div className="v">{fmtCurrency(settings.laborRates.Maint, 2)}<small>/hr</small> <span className="def">default</span></div></div>
        <div className="rate"><div className="l">Arborist</div><div className="v">{fmtCurrency(settings.laborRates.Arborist, 2)}<small>/hr</small> <span className="def edited">edited</span></div></div>
        <div className="rate"><div className="l">Herbicide (sub)</div><div className="v plum">{fmtCurrency(herbSub?.unitRate || settings.subcontractors.preEmergent.unitRate)}<small>/acre</small> <span className="def vendor">vendor</span></div></div>
        <div className="rate"><div className="l">Irrigation</div><div className="v">{fmtCurrency(settings.laborRates.Irrigation, 2)}<small>/hr</small> <span className="def">default</span></div></div>
      </div>
      <div className="te-settings-link">
        <span>Manage rates, production assumptions, modifiers and subcontractors in</span>
        <Button onClick={onSettingsClick}>Estimate settings</Button>
      </div>
    </>
  );
};

const Comparables = ({ selectedTier }) => {
  const margin = Number(selectedTier?.marginPct) || 0;
  return (
    <div className="te-comp">
      <div className="ch">
        <h3>Comparable jobs</h3>
        <span className="n">7 similar properties in your portfolio - same size band + service mix</span>
        <a href="#comparables">View</a>
      </div>
      <div className="bench">
        <div className="b">
          <div className="l">$ per SF</div>
          <div className="v">$0.88</div>
          <div className="you ok">You vs portfolio <span className="pct">+1%</span></div>
          <div className="bar"><i style={{ left: 0, width: '55%' }} /><span className="you-tick" style={{ left: '56%' }} /></div>
        </div>
        <div className="b">
          <div className="l">Margin</div>
          <div className="v">{fmtNumber(margin, 1)}%</div>
          <div className="you ok">Portfolio avg <span className="pct">48%</span></div>
          <div className="bar"><i style={{ left: 0, width: '48%' }} /><span className="you-tick" style={{ left: `${Math.min(95, margin)}%` }} /></div>
        </div>
        <div className="b">
          <div className="l">Win rate at this margin</div>
          <div className="v">67%</div>
          <div className="you warn">Below 55% margin: 89%</div>
          <div className="bar"><i style={{ left: 0, width: '67%', background: 'var(--df-warn)' }} /><span className="you-tick" style={{ left: '67%' }} /></div>
        </div>
      </div>
    </div>
  );
};

const ReviewBanner = ({ checks, onPreview, onSend }) => {
  const passCount = checks.filter(check => check.status === 'pass').length;
  return (
    <div className="te-review">
      <div className="rh">
        <span className="ico">✓</span>
        <h3>Ready to send - {passCount} of {checks.length} checks pass</h3>
      </div>
      <ul>
        {checks.map(check => (
          <li key={check.key} className={check.status === 'warn' ? 'warn' : ''}>{check.label}</li>
        ))}
      </ul>
      <div className="actions">
        <Button onClick={onPreview}>Preview PDF</Button>
        <Button className="primary" onClick={onSend}>Send to client</Button>
      </div>
    </div>
  );
};

const ProductionPane = ({ settings, onSettingsChange }) => {
  const updateProductionRate = (serviceKey, field, value) => {
    const numeric = Number(value);
    onSettingsChange(current => ({
      ...current,
      productionRates: {
        ...current.productionRates,
        [serviceKey]: {
          ...current.productionRates[serviceKey],
          [field]: Number.isFinite(numeric) ? numeric : 0,
        },
      },
    }));
  };

  const updateInference = (key, value) => {
    const numeric = Number(value);
    onSettingsChange(current => ({
      ...current,
      irrigationZoneInference: {
        ...current.irrigationZoneInference,
        [key]: Number.isFinite(numeric) ? numeric : 0,
      },
    }));
  };

  const updateModifier = (key, value) => {
    const numeric = Number(value);
    onSettingsChange(current => ({
      ...current,
      modifierTable: {
        ...current.modifierTable,
        [key]: Number.isFinite(numeric) ? numeric : 0,
      },
    }));
  };

  return (
  <>
    <div className="pane-hd">
      <div>
        <h3>Production rates</h3>
        <p>How fast a crew completes each service. Hours = quantity / rate x modifiers x coverage.</p>
      </div>
      <div className="right">
        <Button onClick={() => onSettingsChange(current => ({
          ...current,
          productionRates: cloneEstimateSettings().productionRates,
        }))}
        >
          Reset to defaults
        </Button>
      </div>
    </div>
    <table className="te-rates-table">
      <thead><tr><th>Service</th><th>Rate</th><th>Quantity from</th><th>Frequency</th><th>Coverage</th></tr></thead>
      <tbody>
        {Object.entries(DEFAULT_PRODUCTION_RATES).map(([key, rateDefinition]) => {
          const rate = settings.productionRates[key] || rateDefinition;
          return (
          <tr key={key}>
            <td className="name">{rateDefinition.label}</td>
            <td>
              <span className="num-in">
                <input value={rate.rate} onChange={(event) => updateProductionRate(key, 'rate', event.target.value)} />
                <span className="u">{RATE_UNIT_LABELS[rateDefinition.unit] || rateDefinition.unit}</span>
              </span>
            </td>
            <td className="src">{rateDefinition.unit === 'lf_hr' ? 'Mapped LF' : rateDefinition.unit === 'trees_hr' ? 'Tree count' : 'Mapped SF / counts'}</td>
            <td>
              <span className="num-in compact">
                <input value={rate.visitsPerYear} onChange={(event) => updateProductionRate(key, 'visitsPerYear', event.target.value)} />
                <span className="u">/yr</span>
              </span>
            </td>
            <td>
              <span className="num-in compact">
                <input
                  value={Math.round((rate.coveragePerVisit || 0) * 100)}
                  onChange={(event) => updateProductionRate(key, 'coveragePerVisit', (Number(event.target.value) || 0) / 100)}
                />
                <span className="u">%</span>
              </span>
            </td>
          </tr>
        );})}
      </tbody>
    </table>
    <div className="pane-hd secondary">
      <div>
        <h3>Irrigation zone inference</h3>
        <p>Divisors used to estimate zones from plant and turf counts.</p>
      </div>
    </div>
    <div className="mods">
      {Object.entries(settings.irrigationZoneInference).map(([key, value]) => (
        <div className="mod-row" key={key}>
          <span className="nm">{key.replace(/([A-Z])/g, ' $1')}</span>
          <span className="v"><input value={value} onChange={(event) => updateInference(key, event.target.value)} /></span>
        </div>
      ))}
    </div>
    <div className="pane-hd secondary">
      <div>
        <h3>Modifier adjustments</h3>
        <p>Multipliers applied to labor hours from area tags.</p>
      </div>
    </div>
    <div className="mods">
      {Object.entries(settings.modifierTable).map(([key, value]) => (
        <div className="mod-row" key={key}>
          <span className="nm">{key.replace(/-/g, ' ')}</span>
          <span className="v"><input value={value} onChange={(event) => updateModifier(key, event.target.value)} /><span className="x">x</span></span>
        </div>
      ))}
    </div>
  </>
  );
};

const AreaDefaultsPane = () => (
  <>
    <div className="pane-hd">
      <div>
        <h3>Default services by area type</h3>
        <p>When a takeoff area is tagged with a type, these services are checked on automatically.</p>
      </div>
      <div className="right"><Button>Reset all</Button></div>
    </div>
    <div className="area-types">
      {Object.entries(AREA_TYPE_LABELS).slice(0, 14).map(([type, label]) => (
        <div className="area-row" key={type}>
          <div className="nm"><span className="swab" style={{ background: AREA_TYPE_COLORS[type] }} />{label}</div>
          <div className="svc-chips">
            {['Mowing', 'Blowing', 'Weed Control', 'Pre-Emergent', 'Irrigation Check', 'Litter Pickup'].map((svc, index) => (
              <span key={svc} className={`svc-chip ${index < 3 ? 'on' : ''}`}>{svc}</span>
            ))}
          </div>
          <button type="button" className="reset">Reset</button>
        </div>
      ))}
    </div>
  </>
);

const SubcontractorsPane = ({ settings, onSettingsChange }) => {
  const updatePreEmergent = (key, value) => {
    const numericFields = new Set(['unitRate', 'minPerVisit']);
    onSettingsChange(current => ({
      ...current,
      subcontractors: {
        ...current.subcontractors,
        preEmergent: {
          ...current.subcontractors.preEmergent,
          [key]: numericFields.has(key) ? Number(value) || 0 : value,
        },
      },
    }));
  };

  return (
  <>
    <div className="pane-hd">
      <div>
        <h3>Subcontractors</h3>
        <p>Services priced by unit instead of in-house labor hours.</p>
      </div>
      <div className="right"><Button className="primary">Add subcontractor</Button></div>
    </div>
    <div className="subs-list">
      <div className="sub-card">
        <div className="nm">Herbicide - Pre-emergent<small>{settings.subcontractors.preEmergent.vendorName} - priced per acre of pre-emergent area</small></div>
        <div className="col">
          <span className="l">Unit rate</span>
          <span className="num-in"><input value={settings.subcontractors.preEmergent.unitRate} onChange={(event) => updatePreEmergent('unitRate', event.target.value)} /><span className="u">$/ac</span></span>
        </div>
        <div className="col">
          <span className="l">Min per visit</span>
          <span className="num-in"><input value={settings.subcontractors.preEmergent.minPerVisit} onChange={(event) => updatePreEmergent('minPerVisit', event.target.value)} /><span className="u">$</span></span>
        </div>
        <div className="col"><span className="l">Quantity from</span><span className="v small">Pre-emergent SF / 43,560</span></div>
        <div className="col">
          <span className="l">Default frequency</span>
          <select value={settings.subcontractors.preEmergent.frequency} onChange={(event) => updatePreEmergent('frequency', event.target.value)}>
            {Object.entries(settings.frequencies).map(([key, freq]) => <option key={key} value={key}>{freq.label}</option>)}
          </select>
        </div>
        <button type="button" className="ed">Edit</button>
      </div>
    </div>
  </>
  );
};

const LaborRatesPane = ({ settings, onSettingsChange }) => {
  const updateLaborRate = (role, value) => {
    onSettingsChange(current => ({
      ...current,
      laborRates: {
        ...current.laborRates,
        [role]: Number(value) || 0,
      },
    }));
  };

  return (
  <>
    <div className="pane-hd"><div><h3>Labor rates</h3><p>Cost per crew hour by role.</p></div></div>
    <table className="te-rates-table">
      <thead><tr><th>Role</th><th>Cost / hr</th><th>Notes</th></tr></thead>
      <tbody>
        {Object.entries(settings.laborRates).map(([role, rate]) => (
          <tr key={role}>
            <td className="name">{role}</td>
            <td><span className="num-in"><input value={rate} onChange={(event) => updateLaborRate(role, event.target.value)} /><span className="u">$/hr</span></span></td>
            <td className="src">{role === 'Arborist' ? 'ISA-certified crew rate' : 'Company default loaded rate'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </>
  );
};

const FrequenciesPane = ({ settings, onSettingsChange }) => {
  const updateFrequency = (key, value) => {
    onSettingsChange(current => ({
      ...current,
      frequencies: {
        ...current.frequencies,
        [key]: {
          ...current.frequencies[key],
          visitsPerYear: Number(value) || 0,
        },
      },
    }));
  };

  return (
  <>
    <div className="pane-hd"><div><h3>Visit frequencies</h3><p>The frequency presets that appear on the estimate screen.</p></div></div>
    <table className="te-rates-table">
      <thead><tr><th>Preset</th><th>Visits / yr</th><th>Used as default for</th></tr></thead>
      <tbody>
        {Object.entries(settings.frequencies).map(([key, freq]) => (
          <tr key={key}>
            <td className="name">{freq.label}</td>
            <td><span className="num-in compact"><input value={freq.visitsPerYear} onChange={(event) => updateFrequency(key, event.target.value)} /><span className="u">/yr</span></span></td>
            <td className="src">{key === 'weekly' ? 'Every-visit maintenance' : key === 'monthly' ? 'Irrigation check, trimming' : '-'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </>
  );
};

const SettingsModal = ({ open, onClose, settings, onSettingsChange }) => {
  const [tab, setTab] = useState('rates');
  if (!open) return null;
  const panes = {
    rates: <ProductionPane settings={settings} onSettingsChange={onSettingsChange} />,
    areas: <AreaDefaultsPane />,
    subs: <SubcontractorsPane settings={settings} onSettingsChange={onSettingsChange} />,
    labor: <LaborRatesPane settings={settings} onSettingsChange={onSettingsChange} />,
    freq: <FrequenciesPane settings={settings} onSettingsChange={onSettingsChange} />,
  };
  return (
    <div className="te-modal-bg open" role="presentation">
      <div className="te-modal" role="dialog" aria-modal="true" aria-label="Estimate settings">
        <div className="modal-hd">
          <div className="gear">⚙</div>
          <div>
            <h2>Estimate settings</h2>
            <div className="subtitle">Company-wide defaults - apply to every new estimate</div>
          </div>
          <button type="button" className="x" onClick={onClose}>x</button>
        </div>
        <div className="modal-body">
          <div className="modal-tabs">
            {[
              ['rates', 'Production rates'],
              ['areas', 'Area type defaults'],
              ['subs', 'Subcontractors'],
              ['labor', 'Labor rates'],
              ['freq', 'Visit frequencies'],
            ].map(([key, label]) => (
              <button type="button" key={key} className={`modal-tab ${tab === key ? 'on' : ''}`} onClick={() => setTab(key)}>
                <span className="ic">{key === 'labor' ? '$' : '▤'}</span> {label}
              </button>
            ))}
          </div>
          <div className="modal-pane">{panes[tab]}</div>
        </div>
      </div>
    </div>
  );
};

const EmptyEstimate = ({ onBackToTakeoff }) => (
  <div className="te-empty">
    <h2>No services selected yet</h2>
    <p>Tag mapped areas with services in Takeoff before building the estimate workspace.</p>
    <Button className="primary" onClick={onBackToTakeoff}>Return to takeoff</Button>
  </div>
);

const handleAutoSelectInput = (event) => {
  const target = event.target;
  if (!target || target.tagName !== 'INPUT') return;
  if (target.type === 'checkbox' || target.type === 'radio') return;
  window.requestAnimationFrame(() => {
    try { target.select(); } catch (_) {}
  });
};

const TakeoffEstimateWorkspace = ({
  jobsite,
  areas = [],
  takeoff,
  rateSeed,
  dirty,
  saving,
  onBackToTakeoff,
  onSaveDraft,
  onGenerateProposal,
  onToast,
}) => {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [dismissedFlag, setDismissedFlag] = useState(false);
  const [enabledByRole, setEnabledByRole] = useState({});
  const [tiers, setTiers] = useState(defaultTiers);
  const [notes, setNotes] = useState('');
  const [rateOverrides, setRateOverrides] = useState({});
  const [settings, setSettings] = useState(() => cloneEstimateSettings(rateSeed));

  useEffect(() => {
    setSettings(current => ({
      ...current,
      laborRates: buildRateOverrides(rateSeed),
    }));
  }, [rateSeed]);

  const laborRateOverrides = useMemo(() => ({
    ...(takeoff?.productionRates || {}),
    ...settings.productionRates,
    _modifiers: toLaborCalculatorModifiers(settings.modifierTable),
  }), [settings.modifierTable, settings.productionRates, takeoff?.productionRates]);

  const hoursResult = useMemo(() => calculateLaborHours(areas, laborRateOverrides), [areas, laborRateOverrides]);
  const baseServices = useMemo(
    () => buildServiceLinesFromTakeoff(areas, hoursResult, settings),
    [areas, hoursResult, settings]
  );
  const services = useMemo(
    () => baseServices.map(service => ({
      ...service,
      enabled: enabledByRole[service.role] ?? service.enabled,
    })),
    [baseServices, enabledByRole]
  );

  const estimate = useMemo(() => ({
    id: takeoff?._id || 'draft-estimate',
    propertyId: jobsite?._id,
    status: 'draft',
    takeoffId: takeoff?._id,
    services,
    tiers,
    rateOverrides,
    notes,
    unmappedAreaCount: areas.filter(area => !area.parentAreaId && !(area.servicesRequired || []).length).length,
  }), [areas, jobsite?._id, services, takeoff?._id, tiers, rateOverrides, notes]);

  const totalLaborHours = services
    .filter(service => service.delivery === 'labor')
    .reduce((sum, service) => sum + computeService(service, settings).hours, 0);
  const selectedTier = tiers.find(tier => tier.recommended) || tiers[1] || tiers[0];
  const checks = runSanityChecks(estimate, settings, {
    serviceMedianAnnualHours: { pruning: 5.5 },
  });
  const jobsiteDisplay = getJobsiteDisplay(jobsite);
  const totalSF = getTotalSF(areas);
  const areaCount = areas.filter(area => !area.parentAreaId).length;

  const hasServices = services.some(service => service.enabled && (service.tasks?.length || service.delivery === 'subcontracted'));

  // Derive the dominant (highest) visits/year per role from the enabled services
  // so the generated proposal reflects what's on screen, not hardcoded cadences.
  const dominantVisitsForRole = (role) => {
    const service = services.find(s => s.role === role && s.enabled);
    if (!service) return 0;
    const freqs = [
      ...(service.tasks || []).map(t => getVisitsPerYear(t.frequency, settings)),
      ...(service.sub ? [getVisitsPerYear(service.sub.frequency, settings)] : []),
    ].filter(v => v > 0);
    return freqs.length ? Math.max(...freqs) : 0;
  };

  const handleGenerate = () => {
    if (onGenerateProposal) {
      onGenerateProposal({
        rates: {
          mtc: settings.laborRates.Maint,
          arborist: settings.laborRates.Arborist,
          herbicide: settings.laborRates.Herbicide,
          irrigation: settings.laborRates.Irrigation,
        },
        margin: (selectedTier?.marginPct || 50) / 100,
        frequencies: {
          mtcVisits: dominantVisitsForRole('Maint') || 52,
          irrigationVisits: dominantVisitsForRole('Irrigation') || 12,
          herbicideVisits: dominantVisitsForRole('Herbicide') || 3,
        },
        estimate,
      });
    } else if (onToast) {
      onToast('Proposal PDF generation is not wired yet');
    }
  };

  if (!hasServices && areaCount > 0) {
    return <EmptyEstimate onBackToTakeoff={onBackToTakeoff} />;
  }

  return (
    <div className="takeoff-estimate desert-flora" onFocusCapture={handleAutoSelectInput}>
      <div className="te-app">
        <div className="te-topbar">
          <div className="crumb">Projects <span className="sep">/</span> <b>{jobsiteDisplay.name}</b> <span className="sep">/</span> New estimate</div>
          <StatusPill />
          <div className="spacer" />
          <Button className="ghost" onClick={onBackToTakeoff}>Discard</Button>
          <Button className="ghost" onClick={() => setSettingsOpen(true)}>Settings</Button>
          <Button onClick={onSaveDraft} disabled={saving || !dirty}>{saving ? 'Saving...' : 'Save draft'}</Button>
          <Button className="primary" onClick={handleGenerate}>Generate proposal PDF</Button>
        </div>

        <div className="te-head">
          <div>
            <h1>{jobsiteDisplay.name}</h1>
            <div className="meta">
              <span><b>#{jobsiteDisplay.code}</b>{jobsiteDisplay.cityState ? ` - ${jobsiteDisplay.cityState}` : ''}</span>
              <span><b>{fmtNumber(totalSF, 0)} SF</b> total mapped</span>
              <span><b>{areaCount}</b> areas mapped</span>
              <span>Crew - <b>{fmtNumber(hoursResult.suggestedCrewSize, 0)} people x {fmtNumber(hoursResult.suggestedVisitDuration, 1)} hrs</b></span>
            </div>
          </div>
          <div className="steps">
            <div className="step done"><span className="n">✓</span> Map areas</div>
            <div className="step done"><span className="n">✓</span> Takeoff</div>
            <div className="step now"><span className="n">3</span> Build estimate</div>
            <div className="step"><span className="n">4</span> Send proposal</div>
          </div>
        </div>

        <div className="te-workspace">
          <div className="te-panel" id="scope">
            <div className="hd">
              <h2>Scope & frequency</h2>
              <div className="sub">
                <span>Edit frequency to update hours</span>
                <span>Annual labor - <b>{fmtNumber(totalLaborHours, 1)} hrs</b></span>
              </div>
            </div>
            {!dismissedFlag && (
              <div className="te-flag-banner">
                <b>Heads up:</b> Low Pruning is compared against portfolio medians. Confirm unusual coverage or frequency before sending.
                <button type="button" className="x" onClick={() => setDismissedFlag(true)}>x</button>
              </div>
            )}
            {services.map(service => (
              <ServiceBlock
                key={service.role}
                service={service}
                settings={settings}
                onToggle={(role) => setEnabledByRole(current => ({ ...current, [role]: !(current[role] ?? service.enabled) }))}
              />
            ))}
            <div className="te-scope-foot">
              <span className="lbl">Annual labor</span>
              <span className="total">{fmtNumber(totalLaborHours, 1)} hrs/yr</span>
              <span className="muted">across {services.filter(service => service.enabled).length} roles</span>
            </div>
          </div>

          <div className="te-panel" id="pricing">
            <div className="hd">
              <h2>Proposal tiers</h2>
              <div className="sub"><span>Adjust margin per tier - scope mirrors left</span></div>
            </div>
            <div className="te-tiers">
              {tiers.map(tier => (
                <TierCard
                  key={tier.id}
                  tier={tier}
                  estimate={estimate}
                  settings={settings}
                  onTierChange={(nextTier) => setTiers(current => current.map(item => item.id === nextTier.id ? nextTier : item))}
                />
              ))}
            </div>
            <RatesStrip settings={settings} services={services} onSettingsClick={() => setSettingsOpen(true)} />
            <Comparables selectedTier={selectedTier} />
            <ReviewBanner
              checks={checks}
              onPreview={() => onToast?.('Preview PDF is not wired yet')}
              onSend={handleGenerate}
            />
          </div>
        </div>
      </div>
      <SettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        settings={settings}
        onSettingsChange={setSettings}
      />
    </div>
  );
};

export default TakeoffEstimateWorkspace;
